import { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Button, Popconfirm, message, Segmented, Modal, Form, Input, Select, Tooltip } from 'antd';
import { ReloadOutlined, SendOutlined, TeamOutlined, EditOutlined, RedoOutlined } from '@ant-design/icons';
import { adminAPI } from '../api/admin';
import { sanitizePhoneInput } from '../utils/phone';

const LANGUAGE_LABELS = {
  fa: '🇮🇷 فارسی',
  en: '🇬🇧 English',
  ja: '🇯🇵 日本語',
  de: '🇩🇪 Deutsch',
  pt: '🇵🇹 Português',
  fr: '🇫🇷 Français',
  it: '🇮🇹 Italiano',
  es: '🇪🇸 Español',
  ko: '🇰🇷 한국어',
  hi: '🇮🇳 हिन्दी',
  zh: '🇨🇳 中文'
};

const languageLabel = (lang) => LANGUAGE_LABELS[lang] || lang || '—';

const formatDateTime = (d) => d ? new Date(d).toLocaleString('fa-IR', { dateStyle: 'medium', timeStyle: 'short' }) : null;

const daysRemaining = (target) => Math.ceil((new Date(target).getTime() - Date.now()) / (24 * 60 * 60 * 1000));

// ستون «زمان ارسال دعوت‌نامه» — برای ورودی‌های دعوت‌شده، زمان دقیق ارسال را نشان
// می‌دهد؛ برای ورودی‌های در انتظار، تاریخ دقیق ارسال خودکار (که سرور بر اساس
// waitingDaysBeforeInvite محاسبه کرده) و تعداد روز باقی‌مانده را نشان می‌دهد.
function ScheduleCell({ entry }) {
  if (entry.isInvited) {
    return (
      <span style={{ color: '#389e0d', fontSize: 13 }}>
        ارسال شد{entry.invitedAt ? ` — ${formatDateTime(entry.invitedAt)}` : ''}
      </span>
    );
  }
  if (entry.scheduledInviteAt) {
    const days = daysRemaining(entry.scheduledInviteAt);
    return (
      <span style={{ fontSize: 13 }}>
        <span style={{ color: '#ad6800' }}>{formatDateTime(entry.scheduledInviteAt)}</span>
        <br />
        <span style={{ color: '#999', fontSize: 11 }}>
          {days > 0 ? `${days} روز دیگر` : 'به‌زودی (در بررسی ساعتی بعدی)'}
        </span>
      </span>
    );
  }
  return (
    <span style={{ color: '#999', fontSize: 13 }}>
      {entry.phone ? 'تأیید نشده' : 'فقط ایمیل (دستی)'}
    </span>
  );
}

export default function WaitlistManager() {
  const [entries, setEntries]   = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(false);
  const [filter, setFilter]     = useState('all'); // all | fa | en
  const [sending, setSending]   = useState({}); // { [id]: true }
  const [resendingSms, setResendingSms] = useState({}); // { [id]: true }
  const [editingEntry, setEditingEntry] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [editForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { lang: filter } : {};
      const res = await adminAPI.getWaitlist(params);
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch {
      message.error('خطا در دریافت لیست انتظار');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const sendInvite = async (entry) => {
    setSending(s => ({ ...s, [entry._id]: true }));
    try {
      const res = await adminAPI.inviteWaitlistEntry(entry._id);
      message.success(res.data.message || 'دعوت‌نامه ارسال شد');
      load();
    } catch (err) {
      message.error(err.response?.data?.message || 'خطا در ارسال دعوت‌نامه');
    } finally {
      setSending(s => ({ ...s, [entry._id]: false }));
    }
  };

  const resendSms = async (entry) => {
    setResendingSms(s => ({ ...s, [entry._id]: true }));
    try {
      const res = await adminAPI.resendWaitlistSms(entry._id);
      if (res.data.success && res.data.smsStatus === 'sent') {
        message.success(res.data.message || 'پیامک با موفقیت ارسال شد');
      } else {
        message.error(res.data.message || 'ارسال پیامک ناموفق بود');
      }
      load();
    } catch (err) {
      message.error(err.response?.data?.message || 'خطا در ارسال مجدد پیامک');
    } finally {
      setResendingSms(s => ({ ...s, [entry._id]: false }));
    }
  };

  const openEdit = (entry) => {
    setEditingEntry(entry);
    editForm.setFieldsValue({
      firstName: entry.firstName || '',
      lastName: entry.lastName || '',
      lang: entry.lang,
      phone: entry.phone || '',
      email: entry.email || ''
    });
  };

  const saveEdit = async () => {
    try {
      const values = await editForm.validateFields();
      setSaving(true);
      await adminAPI.updateWaitlistEntry(editingEntry._id, {
        ...values,
        phone: values.phone || null,
        email: values.email || null
      });
      message.success('ورودی به‌روزرسانی شد');
      setEditingEntry(null);
      load();
    } catch (err) {
      if (err?.errorFields) return; // antd validation error, already shown inline
      message.error(err.response?.data?.message || 'خطا در ویرایش ورودی');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'نام',
      key: 'name',
      render: (_, e) => <strong>{e.firstName || '—'} {e.lastName || ''}</strong>
    },
    {
      title: 'شماره / ایمیل',
      key: 'contact',
      render: (_, e) => <span style={{ direction: 'ltr', display: 'inline-block' }}>{e.phone || e.email || '—'}</span>
    },
    {
      title: 'زبان',
      dataIndex: 'lang',
      key: 'lang',
      width: 90,
      render: l => <Tag color={l === 'fa' ? 'blue' : 'purple'}>{languageLabel(l)}</Tag>
    },
    {
      title: 'تاریخ ثبت',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: d => <span style={{ color: '#888', fontSize: 12 }}>{new Date(d).toLocaleDateString('fa-IR')}</span>
    },
    {
      title: 'وضعیت',
      key: 'status',
      width: 130,
      render: (_, e) => e.isInvited
        ? <Tag color="green">✓ دعوت شد</Tag>
        : e.isVerified
          ? <Tag color="gold">تأیید شده</Tag>
          : <Tag>در انتظار</Tag>
    },
    {
      title: 'زمان ارسال دعوت',
      key: 'schedule',
      width: 190,
      render: (_, e) => <ScheduleCell entry={e} />
    },
    {
      title: 'پیامک تایید ثبت',
      key: 'smsStatus',
      width: 170,
      render: (_, e) => {
        if (!e.isVerified) return <span style={{ color: '#ccc' }}>—</span>;
        if (!e.phone) return <span style={{ color: '#999', fontSize: 12 }}>فقط ایمیل</span>;

        const statusTag = e.smsStatus === 'sent'
          ? <Tag color="green">✓ ارسال شد</Tag>
          : e.smsStatus === 'failed'
            ? <Tooltip title={e.smsError || 'خطای نامشخص'}><Tag color="red">✗ ناموفق</Tag></Tooltip>
            : <Tag>در صف</Tag>;

        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {statusTag}
            <Button
              size="small"
              type="text"
              icon={<RedoOutlined />}
              loading={!!resendingSms[e._id]}
              onClick={() => resendSms(e)}
              title="ارسال مجدد پیامک"
            />
          </span>
        );
      }
    },
    {
      title: 'زمان حرکت قطار',
      key: 'departure',
      width: 200,
      render: (_, e) => (
        e.isInvited && e.invitation?.departureDate
          ? <span>{new Date(e.invitation.departureDate).toLocaleString('fa-IR')} — سکوی {e.invitation.platform || '9¾'}</span>
          : e.isInvited
            ? <span style={{ color: '#999' }}>تعیین نشده</span>
            : <span style={{ color: '#ccc' }}>—</span>
      )
    },
    {
      title: 'عملیات',
      key: 'action',
      width: 150,
      render: (_, e) => {
        if (e.isInvited) return null;
        if (!e.phone) return <span style={{ color: '#999', fontSize: 12 }}>فقط ایمیل</span>;
        return (
          <Popconfirm
            title="ارسال دعوت‌نامه"
            description={`دعوت‌نامه به ${e.firstName || ''} ${e.lastName || ''} (${e.phone}) ارسال شود؟`}
            onConfirm={() => sendInvite(e)}
            okText="بله، ارسال کن"
            cancelText="انصراف"
          >
            <Button size="small" type="primary" icon={<SendOutlined />} loading={!!sending[e._id]}>
              ارسال دعوت‌نامه
            </Button>
          </Popconfirm>
        );
      }
    },
    {
      title: 'ویرایش',
      key: 'edit',
      width: 70,
      render: (_, e) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(e)} />
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Tag icon={<TeamOutlined />} color="magenta" style={{ padding: '4px 10px', fontSize: 13 }}>
          مجموع: {total} نفر
        </Tag>
        <Segmented
          value={filter}
          onChange={setFilter}
          options={[
            { label: 'همه', value: 'all' },
            { label: '🇮🇷 فارسی', value: 'fa' },
            { label: '🌐 English', value: 'en' },
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={load}>بارگذاری مجدد</Button>
      </div>

      <Table
        columns={columns}
        dataSource={entries}
        rowKey="_id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: false }}
        rowClassName={record => record.isInvited ? 'waitlist-invited-row' : ''}
        locale={{ emptyText: 'لیست انتظار خالی است' }}
      />

      <Modal
        title="ویرایش ورودی لیست انتظار"
        open={!!editingEntry}
        onCancel={() => setEditingEntry(null)}
        onOk={saveEdit}
        confirmLoading={saving}
        okText="ذخیره"
        cancelText="انصراف"
        destroyOnClose
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="firstName"
            label="نام"
            rules={[{ required: true, min: 2, message: 'نام باید حداقل ۲ حرف باشد' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="lastName"
            label="نام خانوادگی"
            rules={[{ required: true, min: 2, message: 'نام خانوادگی باید حداقل ۲ حرف باشد' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="lang" label="زبان" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'fa', label: '🇮🇷 فارسی' },
                { value: 'en', label: '🌐 English' }
              ]}
            />
          </Form.Item>
          <Form.Item name="phone" label="شماره موبایل" normalize={sanitizePhoneInput}>
            <Input placeholder="09123456789" style={{ direction: 'ltr' }} />
          </Form.Item>
          <Form.Item name="email" label="ایمیل">
            <Input placeholder="name@example.com" style={{ direction: 'ltr' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
